import { infoCenter, optimizationDownload } from '@e2m-customer-portal/core';
import { useFeatureFlags, useOptimizationStatus, useTokenInfo } from '@e2m-customer-portal/hooks';
import { isDefined, UserRole } from '@e2m-customer-portal/shared';
import { darken, Tab, Tabs } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui-compat';
import { OptimizationFeatures } from '../../generated/graphql';

const useStyles = makeStyles()((theme) => ({
  tab: {
    minWidth: 0,
    minHeight: `calc(${theme.mixins.toolbar.minHeight} - ${theme.spacing(2)}px)`,
    backgroundColor: theme.palette.background.paper,
    margin: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,

    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },

    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary[50],

      '&:hover': {
        backgroundColor: darken(theme.palette.primary[50] as string, 0.05),
      },
    },
  },
}));

export function HeaderMenu(): ReactElement {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const { isOptimizationEnabled, optimizationPortalURL } = useFeatureFlags();
  const optimizationStatus = useOptimizationStatus();
  const { roles } = useTokenInfo();

  const tabs = [
    isOptimizationEnabled && optimizationStatus?.authorizedFeatures.includes(OptimizationFeatures.SCHEDULE_DOWNLOAD)
      ? {
          label: t('OPTIMIZATION_DOWNLOAD_TAB'),
          to: optimizationDownload.getLink(),
          isActive: optimizationDownload.test(pathname),
        }
      : null,
    isOptimizationEnabled &&
    (optimizationStatus?.authorizedFeatures.includes(OptimizationFeatures.PORTAL_REDIRECT) ||
      roles.has(UserRole.VPPOperator)) &&
    optimizationPortalURL
      ? {
          label: t('OPTIMIZATION_PORTAL'),
          to: optimizationPortalURL,
          isActive: false,
          isExternal: true,
        }
      : null,
    {
      label: t('HEADER_MENU_INFO_CENTER'),
      to: infoCenter.getLink(),
      isActive: infoCenter.test(pathname, undefined, { end: true }),
    },
  ].filter(isDefined);

  const activeTabIndex = tabs.findIndex(({ isActive }) => isActive);

  return (
    <Tabs value={activeTabIndex >= 0 ? activeTabIndex : false} classes={{ indicator: 'hidden' }}>
      {tabs.map(({ label, to, isExternal }) => (
        <Tab
          key={to}
          className={classes.tab}
          component={Link}
          to={to}
          label={label}
          target={isExternal ? '_parent' : undefined}
        />
      ))}
    </Tabs>
  );
}
